// @ts-nocheck
import AntSwitch from '@/components/form-control/AntSwitch'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddFlowSensor from '@/features/config/hooks/hydraulic/flowSensor/useAddFlowSensor'
import useEditFlowSensor from '@/features/config/hooks/hydraulic/flowSensor/useEditFlowSensor'
import useGetDetailInput from '@/features/config/hooks/hydraulic/flowSensor/useGetDetailInput'
import useSearchFlowSensorId from '@/features/config/hooks/hydraulic/flowSensor/useSearchFlowSensorId'
import flowSensorApi from '@/api/config/flowSensorApi'
import { CreateFlowSensor, SensorForm } from '@/types/hydraulic'
import {
  decoderLinkFields,
  hasDecoderLink,
  storedDecoderOrUndefined,
  withDecoderId,
} from '@/utils/decoderLink'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Container, Dialog, Stack, Typography } from '@mui/material'
import ConfirmModalUI from '@/components/common/ConfirmModalUI/ConfirmModalUI'
import useSidebarCloseGuard from '@/features/config/hooks/useSidebarCloseGuard'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import classes from './style.module.scss'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { DATA_PAGINATION } from '@/types'

interface SensorProps {
  id?: number
  flowRuleId: number
}

const schema = yup.object({
  address: yup
    .object()
    .required('IO is required')
    .test('has-decoder', 'IO is required', (value) => hasDecoderLink(value as any)),
})

const FormSensorView = (props: SensorProps) => {
  const { t } = useTranslation()
  const { id, flowRuleId } = props
  const { closeSidebar } = useConfigUI()
  const form = useForm<SensorForm>({
    resolver: yupResolver(schema),
  })

  // SCRUM-1032 QC item 6 — backdrop and Escape no longer discard this form, and
  // Cancel asks before throwing away edits.
  const { requestClose, confirmClose, cancelClose, isConfirmOpen } =
    useSidebarCloseGuard(form.formState.isDirty)
  const { interfaceId } = useAuthContext()

  //add
  const { mutate: addSensor, isLoading: loadingAddSensor } = useAddFlowSensor()
  //edit
  const { data: dataItem } = useSearchFlowSensorId(interfaceId, Number(id))
  const { mutate: editSensor, isLoading: loadingEditSensor } = useEditFlowSensor()
  const { data: inputOptions } = useGetDetailInput(interfaceId)
  const { isViewOnly } = usePermission()


  const getOptions = () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
    let data: DATA_PAGINATION | undefined
    data = await flowSensorApi
      .getDetailIO(interfaceId)
      .then((res) => {
        const dataList = res.data?.map((item) => ({
          ...item,
          label: item.name,
          value: `${item.address}-${item.index}-${item.twiId}`,
        })) || []

        return {
          data: dataList,
          page: 1,
          size: dataList.length,
          totalCount: dataList.length,
          totalPage: 1
        }
      })
      .catch((e) => {
        NotifyService.error(e)
        return undefined
      })

    return data
  }

  useEffect(() => {
    if (dataItem) {
      form.reset({
        id: dataItem.id,
        active: dataItem.active,
        address: storedDecoderOrUndefined({
          id: dataItem.decoderId,
          label: dataItem.io,
          value: `${dataItem.address}-${dataItem.index}-${dataItem.twiId}`,
          name : dataItem.io,
          address: dataItem.address,
          index: dataItem.index,
          twiId: dataItem.twiId,
          twiCustomName: dataItem.twiCustomName,
        })
      })
    } else {
      form.reset({
        active: true,
      })
    }
  }, [dataItem, form])

  const handleSave = (values: SensorForm) => {
    if (isViewOnly) return
    const decoder = decoderLinkFields(withDecoderId(values.address, inputOptions))

    const dataCustom: CreateFlowSensor = {
      active: values.active,
      ...decoder,
      interfaceId,
      flowRuleId: flowRuleId,
    }

    if (id) {
      editSensor({
        id: id,
        active: values.active,
        ...decoder,
        interfaceId,
        flowRuleId: dataItem.flowRuleId,
      })
    } else {
      addSensor(dataCustom)
    }
  }

  return (
    <Container sx={{ maxWidth: '450px' }}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <Stack sx={{ padding: '34px 18px' }}>
          <Stack className={classes.sensorHead}>
            <Typography className={classes.titleHead}>
              {id ? t('hydraulicEditFlowSensor') : t('hydraulicAddFlowSensor')}
            </Typography>
            <Stack direction="row">
              <Button
                variant="secondary"
                size="small"
                sx={{ marginRight: '7px', width: '85px' }}
                onClick={requestClose}
                disabled={id ? loadingEditSensor : loadingAddSensor}
              >
                {t('commonActionsCancel')}
              </Button>
              <LoadingButton
                disabled={isViewOnly}
                size="small"
                loading={id ? loadingEditSensor : loadingAddSensor}
                variant="primary"
                sx={{ minWidth: '72px' }}
                type="submit"
              >
                {t('commonActionsSave')}
              </LoadingButton>
            </Stack>
          </Stack>

          <Stack className={classes.sensorContainer}>
            <Stack className={classes.boxOne}>
              <Stack className={classes.leftBox}>
                {!!id ? (
                  <InputLabelField
                    name="id"
                    form={form}
                    label="ID"
                    placeholder="ID"
                    style={{
                      borderRadius: '4px',
                      width: { xs: '100%' },
                    }}
                    padding="8px 16px"
                    disabled
                  />
                ) : (
                  <Stack sx={{ '&>div': { marginTop: '0px'} }}>
                    <Select
                      required
                      minWidth={'301px'}
                      filterFunc={false}
                      label="IO"
                      name="address"
                      // bindLabel="label"
                      bindKey="value"
                      placeholder={t('formsPlaceholdersSelectOne')}
                      form={form}
                      height={'80px'}
                      formatOptionLabel={(item: any) => (
                                          <Stack spacing={0.3}>
                                            <Stack fontWeight={600}>{item.name}</Stack>
                                            {item.address && <Stack>{item.address}-{item.index}</Stack>}
                                            {item.twiCustomName && <Stack>{item.twiCustomName}</Stack>}
                                          </Stack>
                                        )}
                      getOptions={getOptions()}
                    />
                  </Stack>
                )}
              </Stack>

              <Box className={classes.groupActive}>
                <Typography className={classes.textActive}>{t('commonStatusActive')}</Typography>
                <AntSwitch
                  form={form}
                  name="active"
                  width={36}
                  height={19.2}
                  widthThumb={14.4}
                  heightThumb={14.4}
                  borderThumb={14.4}
                  borderTrack={19 / 2}
                  transform={'16'}
                />
              </Box>
            </Stack>

            {!!id && (
              <Stack>
                <Select
                  required
                  minWidth={'301px'}
                  filterFunc={false}
                  label="IO"
                  name="address"
                  // bindLabel="label"
                  bindKey="value"
                  height={'80px'}
                  formatOptionLabel={(item: any) => (
                                          <Stack spacing={0.3}>
                                            <Stack fontWeight={600}>{item.name}</Stack>
                                            {item.address && <Stack>{item.address}-{item.index}</Stack>}
                                            {item.twiCustomName && <Stack>{item.twiCustomName}</Stack>}
                                          </Stack>
                                        )}
                  placeholder={t('formsPlaceholdersSelectOne')}
                  form={form}
                  getOptions={getOptions()}
                />
              </Stack>
            )}
          </Stack>
        </Stack>
      </form>

      {/* SCRUM-1032 QC item 6 — Cancel on a form with edits asks first. */}
      <Dialog open={isConfirmOpen} onClose={cancelClose}>
        <ConfirmModalUI
          title={t('formsUnsavedChangesTitle')}
          text={t('formsUnsavedChangesBody')}
          cancelText={t('formsActionsKeepEditing')}
          confirmText={t('formsActionsDiscardChanges')}
          onClose={cancelClose}
          onConfirm={confirmClose}
        />
      </Dialog>
    </Container>
  )
}

export default FormSensorView
